// FROM: https://github.com/modusign/modusign-client/blob/1f2cc00e317b3cc6ddf42ace319c72036470a423/app/src/components/RequesterTextField/index.tsx

import { Box } from '@modusign/design-system';
import { FontType, TextAlign, TextField } from '@modusign/react-signing/types';
import cx from 'classnames';
import { useCombobox } from 'downshift';
import { useCallback, useMemo, useRef, useState } from 'react';

import { useSafeState } from '@hooks';
import noop from '@libs/noop';

import RoundedXButton from '@app/components/Buttons/RoundedXButton';
import ToolTip from '@app/components/ToolTip';
import TextFieldAutoSuggestion from '@app/features/apply-react-signing/components/FieldRenderer/components/TextFieldAutoSuggestion';
import {
  toAbsolutePoint,
  toAbsoluteSize,
  toRelativePoint,
  toRelativeSize,
} from '@app/features/apply-react-signing/components/FieldRenderer/libs/absRelConverter';
import { createAbsoluteNumber } from '@app/features/apply-react-signing/components/FieldRenderer/libs/createAbsoluteNumber';
import { createAbsoluteSize } from '@app/features/apply-react-signing/components/FieldRenderer/libs/createAbsoluteSize';
import textAlignEnumToCSSProperty from '@app/features/apply-react-signing/components/FieldRenderer/libs/textAlignEnumToCSSProperty';
import UniversalTextField from '@app/features/apply-react-signing/components/FieldRenderer/Setup/components/UniversalTextField';
import {
  AbsolutePoint,
  AbsoluteSize,
} from '@app/features/apply-react-signing/components/FieldRenderer/types';
import RequesterTextFieldContextMenu from '@app/features/apply-react-signing/components/FieldSetupStep/Placeholders/RequesterTextFieldContextMenu';
import { useFontPixelSizes } from '@app/features/apply-react-signing/libs/hooks/useFontPixelSizes';
import useResponsiveValue from '@app/hooks/useResponsiveValue';
import ptToPx from '@app/libs/unitConverters/ptToPx';

import { RequesterFieldBaseProps } from '../props';

import './RequesterTextField.scss';

interface RequesterTextFieldProps extends RequesterFieldBaseProps<TextField> {
  scale: number;
  fieldNumber?: number;
  autoFocus?: boolean;
  suggestions?: string[];
  onTextChange: (value: string) => void;
  onFontSizeChange: (size: number) => void;
  onFontAlignChange: (align: TextAlign) => void;
  onDataLabelChange: (value: string) => void;
  onFontStyleChange: (style: FontType) => void;
}

export function RequesterTextField({
  field,
  viewport,
  scale,
  readOnly,
  isPreview,
  initialContextMenuOpened,
  isDataLabelEditable,
  isDataLabelDuplicated,
  fieldNumber,
  autoFocus,
  suggestions,
  onSizeChange,
  onPointChange,
  onTextChange,
  onFieldRemove,
  onFontSizeChange,
  onFontAlignChange,
  onDataLabelChange,
  onFontStyleChange,
}: RequesterTextFieldProps) {
  const isInMobileBreakpoint = useResponsiveValue([true, false]);
  const [contextMenuOpen, setContextMenuOpen] = useState(initialContextMenuOpened);
  const [focused, setFocused] = useState(false);
  const [message, setMessage] = useSafeState('');
  const fontSizes = useFontPixelSizes(scale);
  const [suggestionAvailable, setSuggestionAvailable] = useState(false);
  const textareaRef = useRef<HTMLInputElement>(null);
  const size = toAbsoluteSize(field.size, viewport);

  const {
    isOpen,
    highlightedIndex,
    openMenu,
    getInputProps,
    selectItem,
    closeMenu,
    getMenuProps,
    getItemProps,
    getComboboxProps,
  } = useCombobox({
    items: suggestions ?? [],
    onInputValueChange: (changes) => {
      if (changes.selectedItem) {
        onTextChange(changes.selectedItem);
      }
    },
    inputValue: field.input?.value ?? '',
  });

  const textAreaProps = useMemo(() => {
    const props = {
      ref: textareaRef,
      placeholder: isPreview ? '' : '추가할 내용을 입력하세요.',
      className: 'RequesterTextField__textarea',
      style: {
        textAlign: textAlignEnumToCSSProperty(field.config.textAlign),
      },
    };
    return suggestionAvailable ? getInputProps(props) : props;
  }, [field.config.textAlign, getInputProps, suggestionAvailable, isPreview]);

  const dataLabelErrorMessage = useMemo(() => {
    if (!field.dataLabel.trim()) {
      return '데이터 라벨 입력은 필수입니다.';
    }

    if (isDataLabelDuplicated) {
      return '중복된 데이터 라벨입니다.';
    }

    return '';
  }, [isDataLabelDuplicated, field]);

  const handleTextChange = useCallback(
    (text: string) => {
      openMenu();
      if (text.length > 300) {
        setMessage('300자를 초과할 수 없습니다. 입력란을 더 추가해 보세요.');
        setTimeout(() => {
          setMessage('');
        }, 3000);
      } else {
        onTextChange?.(text);
      }
    },
    [openMenu, setMessage, onTextChange]
  );

  const handleSpaceOverflow = useCallback(
    ({ isFocusing, isResizing }: { isFocusing: boolean; isResizing: boolean }) => {
      if (isFocusing) {
        setMessage('더 이상 입력할 수 없습니다. 입력란 크기를 키워주세요.');
      } else if (!isResizing) {
        setMessage('입력란이 작습니다. 크기를 키워주세요.');
      }
      setTimeout(() => {
        setMessage('');
      }, 3000);
    },
    [setMessage]
  );

  const handleSizeChange = useCallback(
    (absoluteSize: AbsoluteSize) => {
      const relativeSize = toRelativeSize(absoluteSize, viewport);
      onSizeChange(relativeSize);
    },
    [onSizeChange, viewport]
  );

  const handlePointChange = useCallback(
    (absolutePoint: AbsolutePoint) => {
      const relativePoint = toRelativePoint(absolutePoint, viewport);
      onPointChange(relativePoint);
    },
    [onPointChange, viewport]
  );

  if (!field.input) {
    return null;
  }

  return (
    <UniversalTextField
      autoCompete={
        <>
          {suggestionAvailable && (
            <Box
              height="100%"
              left={0}
              position="absolute"
              top={0}
              width="100%"
              {...getComboboxProps()}
            >
              <TextFieldAutoSuggestion.List
                alignCenter
                isOpen={isOpen && !!suggestions?.length}
                {...getMenuProps()}
              >
                {suggestions?.map((suggestion, index) => (
                  <TextFieldAutoSuggestion.Item
                    highlighted={highlightedIndex === index}
                    {...getItemProps({
                      item: suggestion,
                      index,
                      onClick: (e) => {
                        selectItem(suggestion);
                        e.stopPropagation();
                      },
                    })}
                    key={suggestion}
                    suggestion={suggestion}
                  >
                    {suggestion}
                  </TextFieldAutoSuggestion.Item>
                ))}
              </TextFieldAutoSuggestion.List>
            </Box>
          )}
        </>
      }
      autoFocus={autoFocus}
      dragCancelSelectors={[
        '.RequesterTextField__context-menu-container',
        '.RequesterTextField__delete-button-box',
      ]}
      draggable={!readOnly && !isInMobileBreakpoint}
      editable={!readOnly && !isInMobileBreakpoint}
      enableResizeGrid={!readOnly && !isInMobileBreakpoint}
      fontFamily={field.config.fontStyle.type}
      fontSize={createAbsoluteNumber(ptToPx(field.config.fontStyle.size) * scale)}
      fontSizes={fontSizes}
      point={toAbsolutePoint(field.position, viewport)}
      resizable={!readOnly && !isInMobileBreakpoint}
      rootClassName={cx({
        RequesterTextField: true,
        'RequesterTextField--inserted': field.input.value && !readOnly && !isInMobileBreakpoint,
        'RequesterTextField--focused': focused && !isPreview,
        'RequesterTextField--preview': isPreview,
      })}
      size={size}
      text={field.input.value}
      textareaProps={textAreaProps}
      textareaRef={textareaRef}
      viewport={createAbsoluteSize(viewport)}
      onBlur={() => {
        setFocused(false);
        closeMenu();
      }}
      onChangePoint={handlePointChange}
      onChangeSize={handleSizeChange}
      onChangeText={handleTextChange}
      onClick={() => {
        setContextMenuOpen(true);
      }}
      onFocus={() => setFocused(true)}
      onLineCountChange={(lineCount) => setSuggestionAvailable(lineCount === 1)}
      onOutsideClick={() => {
        if (field.dataLabel && !isDataLabelDuplicated) {
          setContextMenuOpen(false);
        }
      }}
      onSpaceOverflow={handleSpaceOverflow}
      onSpaceRemain={noop}
    >
      {fieldNumber && <div className="RequesterTextField__field-number">{fieldNumber}</div>}
      {!readOnly && !isInMobileBreakpoint && (
        <>
          <div className="RequesterTextField__context-menu-container">
            {contextMenuOpen && (
              <RequesterTextFieldContextMenu
                dataLabel={field.dataLabel}
                dataLabelErrorMessage={dataLabelErrorMessage}
                fontSize={field.config.fontStyle.size}
                fontType={field.config.fontStyle.type}
                isDataLabelEditable={isDataLabelEditable}
                textAlign={field.config.textAlign}
                onChangeDataLabel={onDataLabelChange}
                onChangeFontSize={onFontSizeChange}
                onChangeFontType={onFontStyleChange}
                onChangeTextAlign={onFontAlignChange}
              />
            )}
          </div>
          <div className="RequesterTextField__delete-button-box">
            <RoundedXButton onClick={() => onFieldRemove(field.id)} />
          </div>
        </>
      )}
      {message && (
        <div className="RequesterTextField__overflow-tooltip-box">
          <ToolTip>{message}</ToolTip>
        </div>
      )}
      <div
        className={cx({
          'RequesterTextField__style-box': !isPreview,
          'RequesterTextField__style-box--preview': isPreview,
        })}
      />
    </UniversalTextField>
  );
}
